<template>
  <div class="word-cloud">
    <div v-if="!question_result.results.length" class="not-yet-been-answered">
      <p>{{ parseTranslation(NOT_YET_BEEN_ANSWERED) }}</p>
    </div>

    <div v-else-if="loaderWordCloud" class="loader">
      <h3>{{ parseTranslation(WAIT_WORD_CLOUD) }}</h3>

      <Loader color="#0695fd" :size="50" />
    </div>
    <div
      v-else
      class="word-cloud-wrapper"
      :id="`word-cloud-image-${groupCode}-${question_result.code}`"
    >
      <VueWordCloud
        class="word-cloud-container"
        style="height: 550px; width: 100%"
        :words="wordCloudData"
        :color="([, weight]) => setColor(weight)"
        :font-family="font"
      />

      <MentionsWordCloud
        :minCount="minCount"
        :maxCount="maxCount"
        :wordsCount="wordCloudData.length"
        :colors="colors"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import VueWordCloud from 'vuewordcloud'
import Pptxgen from 'pptxgenjs'
import domtoimage from 'dom-to-image-more'

import { eventBus } from '@/main'

import Loader from '@/components/loading/Loader-3'
import MentionsWordCloud from './MentionsWordCloud'

import { getFirstItem } from '@/utils/array'
import { UNITARY_SHIFT, INDEX_TO_HUMAN_OFFSET } from '@/utils/constants'

import * as translationWordCloud from './wordCloud.translations'

const { default: translations, ...translationKeys } = translationWordCloud
const STARTING_WORD_COUNT = 5
const MIN_COUNT = 1

export default {
  name: 'WordCloud',

  props: {
    question_result: {
      required: true,
      type: Object,
    },

    colors: {
      required: true,
    },

    question_result_index: {
      required: true,
    },

    groupCode: {
      required: true,
    },
  },

  components: {
    VueWordCloud,
    MentionsWordCloud,

    Loader,
  },

  data() {
    return {
      loaderWordCloud: false,
      translations,
      ...translationKeys,
    }
  },

  computed: {
    ...mapGetters(['reportQuestionsDownloadControl']),

    font() {
      return this.question_result?.chartSettings?.font || 'Roboto'
    },

    results() {
      let defaultNumberOfWords = 50

      const questionResults = this.question_result.results

      if (questionResults.length < defaultNumberOfWords) {
        defaultNumberOfWords = questionResults.length
      }

      const numberOfWords =
        this.question_result?.chartSettings?.numberOfWords || defaultNumberOfWords

      const results = questionResults.filter(
        (_, index) => index + UNITARY_SHIFT <= numberOfWords,
      )

      return results
    },

    wordCloudData() {
      const wordCloudData = this.results.map(({ label, count }) => {
        return [label, count]
      })

      return wordCloudData
    },

    maxCount() {
      if (!this.results.length) return 0

      const firstCount = getFirstItem(this.results).count

      return this.results.reduce(
        (acc, result) => (result.count > acc ? result.count : acc),
        firstCount,
      )
    },

    minCount() {
      const firstCount = getFirstItem(this.results).count

      return this.results.reduce(
        (acc, result) => (result.count < acc ? result.count : acc),
        firstCount,
      )
    },

    gap() {
      const FIVE_WORDS = 5

      const numberWordsGreaterThanOrEqualFiveWords =
        this.maxCount >= FIVE_WORDS
          ? this.maxCount / FIVE_WORDS
          : FIVE_WORDS / this.maxCount

      const numberWordsLessThanFiveWords =
        this.maxCount >= this.wordCloudData.length
          ? this.maxCount / this.wordCloudData.length
          : this.wordCloudData.length / this.maxCount

      return this.wordCloudData.length >= FIVE_WORDS
        ? numberWordsGreaterThanOrEqualFiveWords
        : numberWordsLessThanFiveWords
    },
  },

  methods: {
    ...mapActions(['setReportQuestionsDownloadControl']),

    setColor(weight) {
      const colors = this.colors

      if (colors.length === UNITARY_SHIFT) return colors[0]

      return this.determineColor(weight)
    },

    determineColor(weight, wordCount = STARTING_WORD_COUNT) {
      const colorIndex = wordCount - INDEX_TO_HUMAN_OFFSET

      if (wordCount === MIN_COUNT) return this.getColor(colorIndex)

      const sufficientCount = this.validateWeightIsNotLessThanGapWordCount(
        weight,
        wordCount,
      )

      if (sufficientCount) return this.getColor(colorIndex)

      return this.determineColor(weight, wordCount - UNITARY_SHIFT)
    },

    getColor(value) {
      return this.colors[value]
    },

    gapWordCount(value) {
      return this.gap * value
    },

    validateWeightIsNotLessThanGapWordCount(weight, value) {
      return weight >= this.gapWordCount(value)
    },

    async exportPP(index) {
      if (index !== this.question_result_index) return

      if (this.reportQuestionsDownloadControl[this.question_result_index]) {
        return
      }

      this.setReportQuestionsDownloadControl({
        ...this.reportQuestionsDownloadControl,

        [this.question_result_index]: !this.reportQuestionsDownloadControl[
          this.question_result_index
        ],
      })

      const pres = new Pptxgen()

      const image = await this.getWordCloudImage()

      const slide = pres.addSlide()

      slide.addText(this.question_result.title, {
        x: 0.2,
        y: 0.2,
        color: '6b6b6b',
        fontSize: 10,
      })

      slide.addImage({
        data: image,
        x: 1,
        y: 0.8,
        w: '80%',
        h: '80%',
      })

      pres.writeFile({ fileName: 'chart' })

      this.setReportQuestionsDownloadControl({
        ...this.reportQuestionsDownloadControl,

        [this.question_result_index]: !this.reportQuestionsDownloadControl[
          this.question_result_index
        ],
      })
    },

    getWordCloudImage() {
      const node = document.querySelector(
        `#word-cloud-image-${this.groupCode}-${this.question_result.code}`,
      )

      return domtoimage.toPng(node)
    },
  },

  created() {
    this.loaderWordCloud = true

    const FIVE_SECONDS = 5000

    setTimeout(() => {
      this.loaderWordCloud = false
    }, FIVE_SECONDS)

    eventBus.$on('word-cloudExportChart', index => {
      this.exportPP(index)
    })
  },
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Annie+Use+Your+Telescope&family=Anton&family=Bahiana&family=Barrio&family=Finger+Paint&family=Fredericka+the+Great&family=Gloria+Hallelujah&family=Indie+Flower&family=Life+Savers&family=Londrina+Sketch&family=Love+Ya+Like+A+Sister&family=Merienda&family=Nothing+You+Could+Do&family=Pacifico&family=Roboto&family=Montserrat&family=Quicksand:wght@300&family=Righteous&family=Sacramento&family=Shadows+Into+Light&display=swap');

.word-cloud {
  @include flexMiddle;
}

.not-yet-been-answered {
  @include tipography(16);

  .light & {
    color: $gray-2;
  }
}

.loader {
  @include flexMiddle;
  flex-direction: column;

  h3 {
    @include tipography(16);
    margin-bottom: 30px;

    .light & {
      color: $gray-2;
    }
  }
}

.word-cloud-wrapper {
  width: 100%;
}

.word-cloud-container {
  overflow: hidden;
}
</style>
